import { DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useMemo, useState } from 'react'
import { config } from 'v2/pages/settings/flows/list-flows.config'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import type { FlowTypes } from 'v2/types'
import { TriggerItem } from './trigger-item'

interface TriggerSelectionViewProps {
  onExternalAppConfigClick: (appType: FlowTypes) => void
}

export const TriggerSelectionView = ({ onExternalAppConfigClick }: TriggerSelectionViewProps) => {
  const [search, setSearch] = useState('')
  const { slackTriggerEnabled } = useFeatureFlags()
  // SMS/WhatsApp triggers are Enterprise-gated; Enterprise accounts get them
  // unlocked, so they're only locked for non-Enterprise plans.
  const { isEnterpriseLocked } = useUserPlan()

  const filteredConfig = useMemo(() => {
    const source = config(slackTriggerEnabled, isEnterpriseLocked)
    if (!search) return source
    return source
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item &&
            (item.title.toLowerCase().includes(search.toLowerCase()) ||
              item.description.toLowerCase().includes(search.toLowerCase())),
        ),
      }))
      .filter((section) => section.items.length)
  }, [search, slackTriggerEnabled, isEnterpriseLocked])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  return (
    <div className="flex flex-col">
      <DialogHeader className="p-6 pb-4">
        <DialogTitle className="text-h4-500 -mb-1">Select Trigger</DialogTitle>
        <div className="text-p-400 text-secondary">
          Select a trigger that will initiate this agent flow
        </div>
      </DialogHeader>

      <div className="px-6">
        <Input
          value={search}
          onChange={handleSearch}
          placeholder="Search"
          data-testid="input-search"
        />
      </div>

      <ScrollArea className="flex-1 overflow-auto">
        <div className="px-6 pb-6">
          {filteredConfig.map((section) => (
            <div key={section.title} className="mt-4">
              <div className="font-jetbrains text-label uppercase text-muted-foreground">
                {section.title}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {section.items.map(
                  (item) =>
                    item && (
                      <TriggerItem
                        type={item.type}
                        key={`${item.type}-${item.title}`}
                        title={item.title}
                        description={item.description}
                        icon={item.icon}
                        locked={item.locked}
                        isExternalApp={section.title === 'External Apps'}
                        onClick={
                          section.title === 'External Apps'
                            ? () => onExternalAppConfigClick(item.type)
                            : undefined
                        }
                      />
                    ),
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
